import { defineStore } from 'pinia';
import { ref } from 'vue';
import type { ProductWithDetails, ProductBase, ProductLaboratory } from '../types';
import { 
  productsCollection,
  productLaboratoriesCollection,
  STORAGE_KEYS 
} from '../firebase/schema';
import { 
  getDocs, 
  query, 
  where, 
  doc, 
  updateDoc, 
  deleteDoc, 
  addDoc,
  writeBatch
} from 'firebase/firestore';
import { db } from '../firebase/config';
import { useCacheStore } from './cacheStore';
import { useLaboratoryStore } from './laboratoryStore';
import { useAuthStore } from './authStore';

export const useProductStore = defineStore('product', () => {
  // Estado
  const products = ref<ProductWithDetails[]>([]);
  const loading = ref(false);
  const error = ref<string | null>(null);
  
  // Stores relacionados
  const cacheStore = useCacheStore();
  const laboratoryStore = useLaboratoryStore();
  const authStore = useAuthStore();

  // Constantes
  const DELIVERY_TIMES = [
    '24hs',
    '48hs',
    '72hs',
    '5 Dias Habiles',
    '10 Dias Habiles',
    '15 Dias Habiles'
  ] as const;

  // Cargar productos
  const fetchProducts = async () => {
    if (!authStore.user) {
      console.error('Attempted to fetch products without authentication');
      return;
    }

    loading.value = true;
    error.value = null;

    try {
      const isCacheFresh = await cacheStore.checkDataFreshness();
      const cachedProducts = cacheStore.loadFromCache<ProductWithDetails[]>(STORAGE_KEYS.PRODUCTS);

      if (isCacheFresh && cachedProducts) {
        products.value = cachedProducts;
        return;
      }

      const productsSnapshot = await getDocs(productsCollection);
      const productsData = productsSnapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      })) as ProductBase[];

      const processedProducts = await Promise.all(
        productsData.map(async (product) => {
          const relationshipsQuery = query(
            productLaboratoriesCollection,
            where('productId', '==', product.id)
          );
          const relationshipsSnapshot = await getDocs(relationshipsQuery);
          
          const productLabs = relationshipsSnapshot.docs
            .map(doc => {
              const data = doc.data() as ProductLaboratory;
              const laboratory = laboratoryStore.laboratories.find(
                lab => lab.id === data.laboratoryId
              );
              if (!laboratory) return null;
              
              return {
                laboratory,
                details: {
                  ...data,
                  id: doc.id,
                  graduations: data.graduations || [],
                  isCompuesto: data.isCompuesto || false,
                  componentes: data.componentes || []
                }
              };
            })
            .filter(Boolean);

          return {
            ...product,
            laboratories: productLabs
          };
        })
      );

      products.value = processedProducts;
      cacheStore.saveToCache(STORAGE_KEYS.PRODUCTS, processedProducts);

    } catch (err) {
      console.error('Error fetching products:', err);
      error.value = 'Error al cargar productos';
      
      const cachedProducts = cacheStore.loadFromCache<ProductWithDetails[]>(STORAGE_KEYS.PRODUCTS);
      if (cachedProducts) {
        products.value = cachedProducts;
      }
    } finally {
      loading.value = false;
    }
  };

  // Agregar producto
  const addProduct = async (product: ProductWithDetails) => {
    try {
      const productRef = await addDoc(productsCollection, {
        Nombre: product.Nombre,
        Rango: product.Rango || '',
        Categoria: product.Categoria,
        Factor: product.Factor,
        destacado: product.destacado || {
          activo: false,
          mensaje: ''
        },
        similarProducts: product.similarProducts || []
      });

      if (product.similarProducts?.length) {
        const batch = writeBatch(db);
        
        for (const similarId of product.similarProducts) {
          const similarRef = doc(productsCollection, similarId);
          const similarProduct = products.value.find(p => p.id === similarId);
          
          if (similarProduct) {
            const updatedSimilarProducts = [...(similarProduct.similarProducts || [])];
            
            if (!updatedSimilarProducts.includes(productRef.id)) {
              updatedSimilarProducts.push(productRef.id);
            }
            
            for (const otherSimilarId of product.similarProducts) {
              if (otherSimilarId !== similarId && !updatedSimilarProducts.includes(otherSimilarId)) {
                updatedSimilarProducts.push(otherSimilarId);
              }
            }
            
            batch.update(similarRef, { similarProducts: updatedSimilarProducts });
          }
        }
        
        await batch.commit();
      }

      const labBatch = writeBatch(db);
      
      for (const lab of product.laboratories) {
        const labRef = doc(productLaboratoriesCollection);
        labBatch.set(labRef, {
          productId: productRef.id,
          laboratoryId: lab.laboratory.id,
          PrecioDeCosto: lab.details.PrecioDeCosto,
          TiempoDeDemora: lab.details.TiempoDeDemora,
          Disponible: lab.details.Disponible,
          UltimaActualizacion: new Date(),
          graduations: lab.details.graduations || [],
          isCompuesto: lab.details.isCompuesto || false,
          componentes: lab.details.componentes || []
        });
      }

      await labBatch.commit();
      await fetchProducts();
    } catch (err) {
      console.error('Error adding product:', err);
      throw new Error('Error al agregar el producto');
    }
  };

  // Actualizar producto
  const updateProduct = async (productId: string, updatedProduct: ProductWithDetails) => {
    try {
      const currentProduct = products.value.find(p => p.id === productId);
      if (!currentProduct) throw new Error('Producto no encontrado');

      const productRef = doc(productsCollection, productId);
      await updateDoc(productRef, {
        Nombre: updatedProduct.Nombre,
        Rango: updatedProduct.Rango || '',
        Categoria: updatedProduct.Categoria,
        Factor: updatedProduct.Factor,
        destacado: updatedProduct.destacado || {
          activo: false,
          mensaje: ''
        },
        similarProducts: updatedProduct.similarProducts || []
      });

      if (updatedProduct.similarProducts?.length) {
        const batch = writeBatch(db);
        
        const removedSimilarIds = (currentProduct.similarProducts || [])
          .filter(id => !(updatedProduct.similarProducts || []).includes(id));
        
        const newSimilarIds = (updatedProduct.similarProducts || [])
          .filter(id => !(currentProduct.similarProducts || []).includes(id));
        
        const updatedSimilarIds = updatedProduct.similarProducts || [];

        for (const removedId of removedSimilarIds) {
          const removedRef = doc(productsCollection, removedId);
          const removedProduct = products.value.find(p => p.id === removedId);
          
          if (removedProduct?.similarProducts) {
            const updatedSimilarProducts = removedProduct.similarProducts
              .filter(id => id !== productId && updatedSimilarIds.includes(id));
            
            batch.update(removedRef, { similarProducts: updatedSimilarProducts });
          }
        }

        for (const newId of newSimilarIds) {
          const newRef = doc(productsCollection, newId);
          const newProduct = products.value.find(p => p.id === newId);
          
          if (newProduct) {
            const updatedSimilarProducts = [...(newProduct.similarProducts || [])];
            
            if (!updatedSimilarProducts.includes(productId)) {
              updatedSimilarProducts.push(productId);
            }
            
            for (const otherSimilarId of updatedSimilarIds) {
              if (otherSimilarId !== newId && !updatedSimilarProducts.includes(otherSimilarId)) {
                updatedSimilarProducts.push(otherSimilarId);
              }
            }
            
            batch.update(newRef, { similarProducts: updatedSimilarProducts });
          }
        }

        await batch.commit();
      }

      const existingRelationsQuery = query(
        productLaboratoriesCollection,
        where('productId', '==', productId)
      );
      const existingRelations = await getDocs(existingRelationsQuery);
      const labBatch = writeBatch(db);
      
      existingRelations.docs.forEach(doc => {
        labBatch.delete(doc.ref);
      });

      for (const lab of updatedProduct.laboratories) {
        const labRef = doc(productLaboratoriesCollection);
        labBatch.set(labRef, {
          productId: productId,
          laboratoryId: lab.laboratory.id,
          PrecioDeCosto: lab.details.PrecioDeCosto,
          TiempoDeDemora: lab.details.TiempoDeDemora,
          Disponible: lab.details.Disponible ?? true,
          UltimaActualizacion: new Date(),
          graduations: lab.details.graduations || [],
          isCompuesto: lab.details.isCompuesto || false,
          componentes: lab.details.componentes || []
        });
      }

      await labBatch.commit();
      await fetchProducts();
    } catch (err) {
      console.error('Error updating product:', err);
      throw new Error('Error al actualizar el producto');
    }
  };

  // Eliminar producto
  const deleteProduct = async (productId: string) => {
    try {
      const currentProduct = products.value.find(p => p.id === productId);
      if (!currentProduct) throw new Error('Producto no encontrado');

      if (currentProduct.similarProducts?.length) {
        const batch = writeBatch(db);
        
        for (const similarId of currentProduct.similarProducts) {
          const similarRef = doc(productsCollection, similarId);
          const similarProduct = products.value.find(p => p.id === similarId);
          
          if (similarProduct?.similarProducts) {
            const updatedSimilarProducts = similarProduct.similarProducts
              .filter(id => id !== productId);
            
            for (const otherSimilarId of currentProduct.similarProducts) {
              if (otherSimilarId !== similarId && 
                  updatedSimilarProducts.includes(otherSimilarId) &&
                  !similarProduct.similarProducts.some(id => 
                    id !== productId && id !== otherSimilarId && 
                    products.value.find(p => p.id === id)?.similarProducts?.includes(otherSimilarId)
                  )) {
                updatedSimilarProducts.splice(updatedSimilarProducts.indexOf(otherSimilarId), 1);
              }
            }
            
            batch.update(similarRef, { similarProducts: updatedSimilarProducts });
          }
        }
        
        await batch.commit();
      }

      await deleteDoc(doc(productsCollection, productId));

      const relationsQuery = query(
        productLaboratoriesCollection,
        where('productId', '==', productId)
      );
      const relations = await getDocs(relationsQuery);
      const batch = writeBatch(db);
      
      relations.docs.forEach(doc => {
        batch.delete(doc.ref);
      });

      await batch.commit();
      await fetchProducts();
    } catch (err) {
      console.error('Error deleting product:', err);
      throw new Error('Error al eliminar el producto');
    }
  };

  return {
    products,
    loading,
    error,
    DELIVERY_TIMES,
    fetchProducts,
    addProduct,
    updateProduct,
    deleteProduct
  };
});